import { useContext, useEffect, useMemo, useState } from "react";
import { SocketContext } from "../../../contexts/SocketContext";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwTable from "../../shared/OwTable/OwTable";
import IOwTableCols from "../../../models/IOwTableCols";
import IUnit_Of_Measurement_Set from "../../../models/IUnit_Of_Measurement_Set";
import OwButton from "../../shared/OwButton/OwButton";
import { UnitOfMeasureSetsSidebar } from "../UnitOfMeasureSetsSidebar/UnitOfMeasureSetsSidebar";
import ICatalogGetUomSetsDTO from "../../../models/DTO/ICatalogGetUomSetsDTO";
import ICatalogOnGetUomSetsDTO from "../../../models/DTO/ICatalogOnGetUomSetsDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import ICatalogProcessUomSetDTO from "../../../models/DTO/ICatalogProcessUomSetDTO";
import ICatalogOnProcessUomSetDTO from "../../../models/DTO/ICatalogOnProcessUomSetDTO";
import OwLabel from "../../shared/OwLabel/OwLabel";
import UiContext from "../../../contexts/UiContext";
import { ConfirmDeleteOverlayFn } from "../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";

type Props = {};

export const UnitOfMeasureSets = (props: Props) => {
    const socketModel = useContext(SocketContext);
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const uiContext = useContext(UiContext);

    const toggleConfirmDelete = (row: IUnit_Of_Measurement_Set, index: number) => {
        const rowElem = `row-${index}`;

        setDeleteElem(rowElem);
        setRowToDelete(row);
    };

    const editRow = (row: IUnit_Of_Measurement_Set) => {
        const component = (
            <UnitOfMeasureSetsSidebar
                notifyFn={uiContext.notifyFn}
                cancelFn={cancelFn}
                unitOfMeasureSet={row}
                unitOfMeasureSetRelatedUnits={row.Related_Units || []}
            />
        );

        uiContext.setSidebarComponent(component);
        uiContext.toggleSidebar(true);
    };

    const deleteFn = (row: IUnit_Of_Measurement_Set) => {
        processUomSet(row, row?.Related_Units || []);
    };

    const renderActionsFn = (row: IUnit_Of_Measurement_Set, index: number) => {
        return (
            <div className={"d-flex"}>
                <OwDeleteEditActions
                    isEditMode={false}
                    onTrashClickFn={() => toggleConfirmDelete(row, index)}
                    onEditClickFn={() => editRow(row)}
                />
            </div>
        );
    };

    const renderRelatedUnitsFn = (row: IUnit_Of_Measurement_Set) => {
        const details = (row.Related_Units || []).map((relatedUnit, index) => {
            return (
                <div key={relatedUnit.guid} className={"d-flex"}>
                    <div className={"w-5r"}>{relatedUnit.unit_name}</div>
                    <div className={"w-5r"}>{relatedUnit.unit_abbr}</div>
                    <div className={"w-7r text-end"}>{relatedUnit.conversion_factor}</div>
                </div>
            );
        });

        return (
            <div>
                <div className={"d-flex"}>
                    <div className={"w-5r"}>
                        <OwLabel title={"name"} isBold={true} isUnderline={true} />
                    </div>
                    <div className={"w-5r"}>
                        <OwLabel title={"abbr"} isBold={true} isUnderline={true} />
                    </div>
                    <div className={"w-7r text-end"}>
                        <OwLabel title={"conv_factor"} isBold={true} isUnderline={true} />
                    </div>
                </div>
                {details}
            </div>
        );
    };

    const getTableCols = (): IOwTableCols[] => {
        return [
            {
                title: "",
                valueProp: "",
                renderFn: renderActionsFn,
                renderFnEmitsRow: true,
                className: "w-7r",
            },
            {
                title: "name",
                valueProp: "unit_name",
            },
            {
                title: "abbr",
                valueProp: "unit_abbr",
            },
            {
                title: "related_units",
                valueProp: "",
                renderFn: renderRelatedUnitsFn,
                renderFnEmitsRow: true,
            },
        ];
    };

    const [tableCols, setTableCols] = useState<IOwTableCols[]>(getTableCols());
    const [dataSet, setDataSet] = useState<IUnit_Of_Measurement_Set[]>([]);
    const [isDataFetching, setIsDataFetching] = useState(true);
    const [isInitialData, setIsInitialData] = useState(true);
    const [rowToDelete, setRowToDelete] = useState<IUnit_Of_Measurement_Set | null>(null);
    const [deleteElem, setDeleteElem] = useState<string | null>(null);
    const crumbs = useMemo(() => {
        return [
            { title: "catalogs", url: "/catalogs" },
            { title: "uom_sets", url: "/catalogs/uom_sets" },
        ] as IBreadcrumbs[];
    }, []);

    const getUomSets = () => {
        const payload: ICatalogGetUomSetsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socketModel?.emit(EndpointsEnum.CatalogsGetUomSets, payload);

        setIsDataFetching(true);
    };

    const onGetUomSets = (data: ICatalogOnGetUomSetsDTO) => {
        setDataSet(data.uomSets);
        setIsDataFetching(false);
        setIsInitialData(false);
    };

    const processUomSet = (uomSet: IUnit_Of_Measurement_Set, relatedUnits: IUnit_Of_Measurement_Set[]) => {
        const payload: ICatalogProcessUomSetDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            uomSet,
            relatedUnits,
            deleteFlag: true,
        };

        socketModel?.emit(EndpointsEnum.CatalogsProcessUomSet, payload);
    };

    const onProcessUomSet = (data: ICatalogOnProcessUomSetDTO) => {
        cancelDelete();
        getUomSets();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CatalogsOnGetUomSets, fn: onGetUomSets },
        { endpoint: EndpointsEnum.CatalogsOnProcessUomSet, fn: onProcessUomSet },
    ];

    useEffect(() => {
        for (const socket of sockets) {
            socketModel?.on(socket.endpoint, socket.fn);
        }

        getUomSets();

        // set the title
        document.title = "UoM Sets";

        return () => {
            for (const socket of sockets) {
                socketModel?.removeListener(socket.endpoint, socket.fn);
            }
        };
    }, []);

    const cancelFn = (refresh?: boolean) => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);

        if (refresh) {
            getUomSets();
        }
    };

    const create = () => {
        const component = <UnitOfMeasureSetsSidebar notifyFn={uiContext.notifyFn} cancelFn={cancelFn} />;

        uiContext.setSidebarComponent(component);
        uiContext.toggleSidebar(true);
    };

    const cancelDelete = () => {
        setRowToDelete(null);
        setDeleteElem(null);
    };

    const commitDelete = () => {
        console.log("commitDelete: ", rowToDelete);
        if (rowToDelete) {
            deleteFn(rowToDelete);
        }
    };

    return (
        <div className={"UnitOfMeasureSets position-relative"}>
            <Breadcrumbs crumbs={crumbs} />

            <div className={"d-flex align-items-center mt-4"}>
                <OwTitle title={"uom_sets"} leftIconName={"fad fa-cart-flatbed-boxes"} />
                <div className={"ms-2"}>
                    <OwButton className={"btn-warning"} title={"create"} leftIconClass={"fad fa-plus"} fn={create} />
                </div>
            </div>

            <div className="mt-3 position-relative">
                <ConfirmDeleteOverlayFn
                    targetElem={deleteElem}
                    handleConfirm={commitDelete}
                    handleCancel={cancelDelete}
                    leftAdjustment={0}
                    nestedParentElem={"uom-sets-table"}
                    skipMainView={true}
                />

                <OwTable
                    id={"uom-sets-table"}
                    columns={tableCols}
                    dataSet={dataSet}
                    userModel={userModel}
                    supplierModel={supplierModel}
                    dataFetch={isDataFetching}
                    initialData={isInitialData}
                />
            </div>
        </div>
    );
};
